import { projectTypeToSlotKey } from '@jobos/shared';
import type { JobDetail } from '@jobos/shared/types/job';
import { BreakdownDocsWrapper } from './BreakdownDocsWrapper';
import { SingleCategoryBreakdownView } from './SingleCategoryBreakdownView';
import { CARPET_CONFIG, EQUIPMENT_CONFIG } from './singleCategoryConfigs';
import { JobDetailBreakdown } from './JobDetailBreakdown';

interface JobBreakdownProps {
  job: JobDetail;
  onDirtyChange: (dirty: boolean) => void;
}

export function JobBreakdown({ job, onDirtyChange }: JobBreakdownProps) {
  const config =
    job.projectType === 'Carpet Cleaning' ? CARPET_CONFIG
    : job.projectType === 'Equipment Rental' ? EQUIPMENT_CONFIG
    : null;

  // Slot the breakdown is mounted for ('carpet' | 'generic'). Documents are
  // scoped per slot, so keying the wrapper on slotKey forces a remount on a
  // project-type switch — resetting its resolver state (the auto-create guard)
  // and re-fetching the new slot's document list instead of serving the old
  // slot's cached one.
  const slotKey = projectTypeToSlotKey(job.projectType);

  if (config) {
    return (
      <BreakdownDocsWrapper key={slotKey} jobId={job.id} slotKey={slotKey}>
        {({ documentId }) => (
          // key includes config.category so a project-type switch REMOUNTS the
          // view. This is LOAD-BEARING: SingleCategoryBreakdownView calls
          // config.useCatalog()/useEnsureLineItem() unconditionally, so the
          // config (and thus which hooks run) must never change on a live
          // mounted instance — a fresh key guarantees a remount instead.
          <SingleCategoryBreakdownView
            key={`${config.category}:${documentId ?? 'legacy'}`}
            config={config}
            jobId={job.id}
            jobCode={job.jobCode}
            jobProjectType={job.projectType}
            documentId={documentId}
            onDirtyChange={onDirtyChange}
          />
        )}
      </BreakdownDocsWrapper>
    );
  }

  return (
    <BreakdownDocsWrapper key={slotKey} jobId={job.id} slotKey={slotKey}>
      {({ documentId }) => (
        <JobDetailBreakdown
          key={documentId ?? 'legacy'}
          jobId={job.id}
          jobCode={job.jobCode}
          jobProjectType={job.projectType}
          activeBreakdownVersionId={job.activeBreakdownVersionId}
          breakdownModifiedSinceVersion={job.breakdownModifiedSinceVersion}
          onDirtyChange={onDirtyChange}
          documentId={documentId}
        />
      )}
    </BreakdownDocsWrapper>
  );
}
